import { cn } from '@/lib/utils';
import type {
  LeaderboardEntry,
  LeaderboardMetricScore,
} from '@/sections/customerManagement/types';
import {
  METRIC_DISPLAY_LABELS,
  METRIC_DISPLAY_ORDER,
} from '@/sections/customerManagement/utils/leaderboardTransforms';

const SEVERITY_CONFIG = [
  {
    key: 'critical',
    label: 'Critical',
    barClass: 'bg-chariot-status-critical',
  },
  { key: 'high', label: 'High', barClass: 'bg-chariot-status-high' },
  { key: 'medium', label: 'Medium', barClass: 'bg-chariot-status-medium' },
  { key: 'low', label: 'Low', barClass: 'bg-chariot-status-low' },
  { key: 'info', label: 'Info', barClass: 'bg-chariot-status-info' },
] as const;

type SeverityKey = (typeof SEVERITY_CONFIG)[number]['key'];

const TRIAGE_METRICS = new Set(['accurate_triage', 'effective_triage']);

function hasSeverityCounts(metric: LeaderboardMetricScore): boolean {
  return SEVERITY_CONFIG.some(s => (metric[s.key as SeverityKey] ?? 0) > 0);
}

function getSeverityTotal(metric: LeaderboardMetricScore): number {
  return SEVERITY_CONFIG.reduce(
    (sum, s) => sum + (metric[s.key as SeverityKey] ?? 0),
    0
  );
}

interface MetricRowProps {
  metricName: string;
  metric: LeaderboardMetricScore;
}

function MetricRow({ metricName, metric }: MetricRowProps) {
  const total = getSeverityTotal(metric);
  const isTriage = TRIAGE_METRICS.has(metricName);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-chariot-text-primary text-sm font-medium">
          {METRIC_DISPLAY_LABELS[metricName] ?? metricName}
        </span>
        <div className="text-chariot-text-secondary flex items-center gap-3 text-xs">
          {SEVERITY_CONFIG.map(
            s =>
              (metric[s.key as SeverityKey] ?? 0) > 0 && (
                <span key={s.key} className="flex items-center gap-1">
                  <span
                    className={cn(
                      'inline-block h-2 w-2 rounded-full',
                      s.barClass
                    )}
                  />
                  {metric[s.key as SeverityKey]} {s.label}
                </span>
              )
          )}
        </div>
      </div>

      {total > 0 && (
        <div className="bg-chariot-surface-secondary flex h-2.5 w-full overflow-hidden rounded-full">
          {SEVERITY_CONFIG.map(s => {
            const count = metric[s.key as SeverityKey] ?? 0;
            if (count === 0) return null;
            const pct = (count / total) * 100;
            return (
              <div
                key={s.key}
                className={cn('h-full', s.barClass)}
                style={{ width: `${pct}%` }}
                title={`${s.label}: ${count}`}
              />
            );
          })}
        </div>
      )}

      {isTriage &&
        (metric.accurate != null || metric.falsePositive != null) && (
          <div className="text-chariot-text-secondary flex gap-4 text-xs">
            {metric.accurate != null && (
              <span>Accurate: {metric.accurate}</span>
            )}
            {metric.falsePositive != null && (
              <span>False Positive: {metric.falsePositive}</span>
            )}
          </div>
        )}
    </div>
  );
}

interface Props {
  entry: LeaderboardEntry;
}

export function MetricBreakdownChart({ entry }: Props) {
  const metricsWithCounts = METRIC_DISPLAY_ORDER.filter(name => {
    const metric = entry.metrics[name];
    return metric && hasSeverityCounts(metric);
  });

  if (metricsWithCounts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {metricsWithCounts.map(name => (
        <MetricRow key={name} metricName={name} metric={entry.metrics[name]} />
      ))}
    </div>
  );
}
